import React from 'react'
import { useEnvValue } from '@morningtrain/react-helpers'
import { useValue } from '@morningtrain/react-resources'
import {useTranslation} from "@morningtrain/react-translations";

function InventoryItemCalculator ({ inventoryItemId }) {
  const amountPerUnit = useValue('amount_per_unit')
  const pricePerUnit = useValue('price')
  const unitStep = useValue('min_unit')
  const normValues = useValue('future_norm_values', {})
  const maxOrderQuantity = useValue('max_order_quantity')
  const minOrderPrice = useValue('minimum_order_price')
  const deliveryDateStr = useValue('delivery_date')

  const [weekdays] = useEnvValue('globals.inventory.delivery_days.weekdays', [])
  const [dates] = useEnvValue('globals.inventory.delivery_days.dates', [])

  const [norm, setNorm] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'norm'].join('.'), 0)
  const [holdings, setHoldings] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'holdings'].join('.'))
  const [suggestion, setSuggestion] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'suggestion'].join('.'), 0)
  const [adjustment, setAdjustment] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'adjustment'].join('.'), 0)
  const [total, setTotal] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'total'].join('.'), 0)
  const [totalAmount, setTotalAmount] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'total_amount'].join('.'), 0)
  const [totalPrice, setTotalPrice] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'total_price'].join('.'), 0)
  const [errorMaxTotalAmount, setErrorMaxTotalAmount] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'error_max_total_amount'].join('.'), 0)
  const [errorMinTotalAmount, setErrorMinTotalAmount] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'error_min_total_amount'].join('.'), 0)
  const [minimumOrderPrice, setMinimumOrderPrice] = useEnvValue('globals.inventory.minimum_order_price', 0)

  const [errorDeliveryDate, setErrorDeliveryDate] = useEnvValue(['globals.inventory.order_items', inventoryItemId, 'error_delivery_date'].join('.'), false)

  const deliveryDate = deliveryDateStr ? new Date(deliveryDateStr) : null;
  let weekday, month, day, year;
  if (deliveryDate) {
    weekday = deliveryDate.getDay()
    weekday = useTranslation('generic.days.' + weekday)
    month = deliveryDate.getMonth()
    month = useTranslation('generic.months.' + month)
    day = deliveryDate.getDate()
    year = deliveryDate.getFullYear()
  }

  let cantBeOrdered = false;

  React.useEffect(() => {
    setErrorDeliveryDate(false);
    if (deliveryDate == null) {
      return;
    }
    const deliveryDateTimestamp = deliveryDate.valueOf();
    const datesArranged = [];
    const datesArrangedTimestamps = [];
    for (const date in dates) {
      const curDate = new Date(dates[date]);
      const timestamp = curDate.valueOf()
      datesArranged[timestamp] = curDate.toUTCString();
    }

    for (const item1 in datesArranged) {
      datesArrangedTimestamps.push(parseInt(item1))
    }

    datesArrangedTimestamps.sort(function(a, b) {
      return a - b;
    });

    if (datesArrangedTimestamps[0] < deliveryDateTimestamp) {
      setErrorDeliveryDate(weekday + ' ' + day + ' ' + month + ' ' + year)
      cantBeOrdered = true;
    }
  }, [deliveryDate])

  React.useEffect(() => {
    let normSum = 0

    weekdays.forEach(weekday => {
      const date = dates[weekday]
      if (normValues && normValues[date]) {
        normSum += normValues[date] || 0
      }
    })

    setNorm(normSum)
  }, [normValues, weekdays])

  React.useEffect(() => {
    if (holdings === null || typeof holdings === 'undefined') {
      return
    }

    let suggestion = Math.max(norm - holdings, 0)

    if (isNaN(suggestion)) {
      suggestion = 0
    }

    if (unitStep > 1) {
      suggestion = Math.ceil(suggestion / unitStep) * unitStep
    }

    setSuggestion(suggestion)
  }, [unitStep, norm, holdings])

  React.useEffect(() => {
    if (cantBeOrdered) {
      setTotal(0);
      return;
    }
    setErrorMinTotalAmount(null)
    setErrorMaxTotalAmount(null)
    let preTotal = suggestion + adjustment
    let minAmount = parseInt(unitStep)
    if (Number.isInteger(maxOrderQuantity) && preTotal > maxOrderQuantity) {
      setErrorMaxTotalAmount(maxOrderQuantity)
      setTotal(minAmount <= maxOrderQuantity ? maxOrderQuantity : 0)
      return
    }
    if (preTotal < minAmount && preTotal > 0) {
      setErrorMinTotalAmount(minAmount)
      setTotal(minAmount <= maxOrderQuantity ? minAmount : 0)
      return
    }
    setErrorMinTotalAmount(null)
    setErrorMaxTotalAmount(null)
    setTotal(Math.max(preTotal, 0))
  }, [suggestion, adjustment, maxOrderQuantity, unitStep])

  React.useEffect(() => {
    setTotalAmount(total * amountPerUnit)
    setTotalPrice(total * pricePerUnit)
  }, [total, amountPerUnit, pricePerUnit])

  React.useEffect(() => {
    setMinimumOrderPrice(minOrderPrice)
  }, [minOrderPrice])

  return null
}

export default InventoryItemCalculator
